/** @w/crdt facade; contract: ../docs/facade.spec.md. */
// Instantiates the wasm for this runtime (package.json "imports": workerd / browser / default).
// oxlint-disable-next-line import/no-unassigned-import -- side-effect init
import '#crdt-wasm-load'
import * as w from './wasm/crdt_wasm.js'
import { decoded, decodeView, holdsRefs } from './view-bytes'

export type Json = null | boolean | number | string | Json[] | { [key: string]: Json }
export type ReplicaId = number
export type Version = Readonly<{ epoch: number; vector: ReadonlyMap<ReplicaId, number> }>
type Session = { client?: ReplicaId; user?: string }

export class EditRejected extends Error {}

/** Old validateJson (src/doc.ts:1182): finite numbers, plain objects, dense arrays, no cycles, no `undefined`. */
function validateJson(value: unknown, ancestors = new Set<object>()): asserts value is Json {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return
  if (typeof value === 'number' && Number.isFinite(value)) return
  if (!value || typeof value !== 'object' || ancestors.has(value)) throw new EditRejected('Invalid JSON')
  if (!Array.isArray(value) && Object.getPrototypeOf(value) !== Object.prototype && Object.getPrototypeOf(value) !== null) throw new EditRejected('Expected plain JSON')
  if (Array.isArray(value)) for (let index = 0; index < value.length; index++) {
    if (!Object.hasOwn(value, index)) throw new EditRejected('Invalid JSON: sparse array')
  }
  ancestors.add(value)
  for (const child of Object.values(value)) validateJson(child, ancestors)
  ancestors.delete(value)
}

/** Spec "Author": the user every change of this JS realm is stamped with (requirements :68), unless the doc's session
 * names one. Set by the layer that sets the connection's session user, so the stamp equals the engine sender. */
let acting: string | undefined
export function actAs(user: string | null | undefined): void { acting = user ?? undefined }
/** The user a change begun now is stamped with: the doc's session user, else the realm's acting user, else '' (the
 * engine refuses '' with "sender is unset" / "by \"\" is not admitted"). */
function author(session: string | undefined): string { return session ?? acting ?? '' }

class Artifact {
  readonly #bytes: Uint8Array
  constructor(bytes: Uint8Array) { this.#bytes = bytes }
  get bytes(): Uint8Array { return this.#bytes.slice() }
  get byteLength(): number { return this.#bytes.length }
  /** Internal: the bytes without a copy. */
  raw(): Uint8Array { return this.#bytes }
}
export class Snapshot extends Artifact {}
export class Update extends Artifact {}
export type Encoded = Uint8Array | Snapshot | Update
export type RemoteUpdate = Encoded

/** Spec "Undo": one inverse per local op, addressed by original node id. */
type Inverse =
  | { kind: 'join'; node: string; destination: string; join: string }
  | { kind: 'unjoin'; node: string; destination: string; join: string }
  | { kind: 'attr'; node: string; key: string; raw: string | undefined }
  | { kind: 'delete'; node: string }
  | { kind: 'restore'; node: string; parent: string; left: string | null; index: number }
  /** `first`: the first unit the op inserted (its `remove` units have consecutive clocks, one Yjs item); `left`/`right`:
   * the units beside the edit after it (null: the text's edge); `removedUnits`: the units it removed, as runs of
   * consecutive clocks, so their re-insertion forwards them. Anchored inverses are replayed by identity. */
  | { kind: 'text'; node: string; at: number; remove: number; insert: string; first?: string | null; left?: string | null; right?: string | null; removedUnits?: readonly (readonly [string, number])[] }
  | { kind: 'move'; node: string; parent: string; left: string | null }
  | { kind: 'type'; node: string; type: string }
type History = { token: string; inverses: Inverse[]; group?: string }

/** Old ViewDelta subset the change feeds read (editor view-delta.ts:14): the views, plus ids this edit moved. */
export type ViewDelta = { before: Json; after: Json; movedIds?: readonly string[] }
/** `repair`: an A1 follower repair an import made (Doc.applyUpdate), not a user edit: no history entry. */
/** `textOnly`: every op of the change edited text, so its merge applies no row (crdt_applied reports no text edit). */
export type Change = { version: Version; delta: ViewDelta; readonly update: Update; changed: boolean; textOnly?: true; historyToken?: string; historyTokens?: readonly string[]; repair?: true }

const freed = new FinalizationRegistry<number>(handle => { w.free(handle) })
const SNAPSHOT_MAGIC = [0x43, 0x52, 0x53, 0x33] // "CRS3", freeze.rs encode_snapshot

function newReplica(): ReplicaId {
  const words = crypto.getRandomValues(new Uint32Array(2))
  return (words[0] & 0x1fffff) * 0x100000000 + words[1]
}

function isSnapshot(bytes: Uint8Array): boolean {
  return SNAPSHOT_MAGIC.every((byte, index) => bytes[index] === byte)
}

/** crdt_rs StaleEpoch (update.rs:635): an update from an epoch the dropped freeze map cannot translate. The doc is
 * unchanged; the sender reloads the current snapshot (facade spec "Freeze"). */
export class StaleEpoch extends EditRejected {
  constructor(readonly updateEpoch: number, readonly docEpoch: number) {
    super(`StaleEpoch: update epoch ${updateEpoch}, document epoch ${docEpoch}`)
  }
}

function call<T>(body: () => T): T {
  try {
    return body()
  } catch (error) {
    if (error instanceof EditRejected) throw error
    const message = error instanceof Error ? error.message : String(error)
    const stale = /StaleEpoch \{ update_epoch: (\d+), doc_epoch: (\d+) \}/u.exec(message)
    if (stale) throw new StaleEpoch(Number(stale[1]), Number(stale[2]))
    throw new EditRejected(message)
  }
}

type Child = { node: string; type: string; id: string }
function children(h: number, node: string): Child[] {
  const out = w.children(h, node)
  if (!out) return []
  return out.split('\n').map(line => {
    const [child, type, id] = line.split('\t')
    return { node: child, type, id }
  })
}
const isAux = (type: string): boolean => type.includes('.') || type.startsWith('json:')
/**
 * The inverse of a text op that inserted `inserted` units at `at` of `node` and removed `removed`, read after the op:
 * anchored by unit identity, as Yjs UndoManager undoes by item id, so a peer edit elsewhere in the text moves it.
 */
function textInverse(h: number, node: string, at: number, inserted: number, removed: string,
  removedUnits: readonly (readonly [string, number])[]): Inverse {
  const first = inserted > 0 ? w.text_id_at(h, node, at) ?? null : null
  // One insert is one run of consecutive clocks; a run that is not stays offset-based.
  const run = first === null || w.text_id_at(h, node, at + inserted - 1) === unitAfter(first, inserted - 1)
  const left = at > 0 ? w.text_id_at(h, node, at - 1) ?? null : null
  const right = w.text_id_at(h, node, at + inserted) ?? null
  return run ? { kind: 'text', node, at, remove: inserted, insert: removed, first, left, right, removedUnits }
    : { kind: 'text', node, at, remove: inserted, insert: removed }
}

/** Units as runs of consecutive clocks: `[first, count]`. */
function unitRuns(units: readonly string[]): [string, number][] {
  const runs: [string, number][] = []
  for (const unit of units) {
    const last = runs.at(-1)
    if (last && unitAfter(last[0], last[1]) === unit) last[1] += 1
    else runs.push([unit, 1])
  }
  return runs
}

function unitAfter(unit: string, offset: number): string {
  const [client, clock] = unit.split(':')
  return `${client}:${Number(clock) + offset}`
}

/** Where unit `unit` is now in `node`'s text: after it (`left`) or at it (`right`); undefined when it is elsewhere. */
function unitGap(h: number, node: string, unit: string, side: 'left' | 'right'): number | undefined {
  const found = w.text_index_of(h, unit)
  if (!found) return undefined
  const [owner, index, deleted] = found.split('\t')
  if (owner !== node) return undefined
  return Number(index) + (side === 'left' && deleted === '0' ? 1 : 0)
}

function utf16(units: Uint16Array): string {
  let out = ''
  for (let at = 0; at < units.length; at += 8192) out += String.fromCharCode(...units.subarray(at, at + 8192))
  return out
}

type Locus =
  | { kind: 'map'; node: string; type: string }
  | { kind: 'list'; node: string; own: boolean }
  | { kind: 'opts'; node: string }
  | { kind: 'text'; node: string }
  | { kind: 'attr'; node: string; type: string; key: string }

export class Doc {
  readonly #client: ReplicaId
  readonly #user: string | undefined
  #view: Json | undefined
  #editing = false
  #undo: History[] = []
  #redo: History[] = []
  #tokens = 0
  /** Text units undo/redo re-inserted: an old unit maps to the unit that replaced it, as Yjs follows `item.redone`
   * (followRedone), so an older inverse anchored to the old unit finds the live one. Merged after an edit succeeds. */
  /** @internal */ units = new Map<string, string>()
  /** Block id -> node of local deletions, for readItem/insertRetained of a detached entity (old doc.ts:906-933). */
  /** @internal */ tombstones = new Map<string, string>()
  /** Spec "Move": block items removed in the open edit, node -> restore anchor. They stay in crdt_rs until the edit ends
   * (hidden from reads and indices), so an insertRetained in the same edit moves the node instead of copying it, and a
   * concurrent edit inside it survives (old core insertRetained was a membership change, easy 0b86a007f doc.ts:906). */
  /** @internal */ limbo = new Map<string, { id: string; inverse: Extract<Inverse, { kind: 'restore' }> }>()
  /** Blocks the open edit built, id -> node: a path step or readItem finds them without searching (find_child scans
   * the list, find_block walks every node), as long as the node is not deleted again. Cleared when the edit ends. */
  /** @internal */ readonly built = new Map<string, string>()

  /** Spec "Runtime keys": `$` fields by block id, overlaid on the view and never written to the engine. */
  /** @internal */ readonly runtime = new Map<string, Record<string, Json>>()
  /** Inputs whose `$` fields are read once the block they built is in the view (block id -> input). */
  /** @internal */ readonly pending = new Map<string, Json>()
  #rootInput: Json | undefined
  /** Set once a view carried runtime fields, so a later removal of the last one still rewrites the view. */
  #overlaid = false

  constructor(json: Json | undefined, _envelope?: unknown, session: Session = {}, handle?: number) {
    this.#client = session.client ?? newReplica()
    this.#user = session.user
    if (handle === undefined) validateJson(json)
    this.h = handle ?? call(() => w.create(JSON.stringify(json), this.#client, author(this.#user)))
    if (handle === undefined && hasRuntimeKeys(json)) this.#rootInput = json
    freed.register(this, this.h, this)
  }

  /** @internal */ overlay(view: Json): Json {
    if (this.#rootInput !== undefined) {
      captureRuntime(this.#rootInput, view, this.runtime)
      this.#rootInput = undefined
    }
    if (this.runtime.size === 0 && this.pending.size === 0 && !this.#overlaid) return view
    this.#overlaid = true
    return overlayRuntime(view, this)
  }

  #restore<V>(target: Map<string, V>, from: Map<string, V>): void {
    target.clear()
    for (const [key, value] of from) target.set(key, value)
  }

  /** @internal */ readonly h: number

  /** @internal */ static wrap(handle: number, session: Session): Doc { return new Doc(undefined, undefined, session, handle) }

  free(): void {
    freed.unregister(this)
    w.free(this.h)
  }

  /** Deeply frozen view, cached until the next edit or import. */
  toJSON(): Json {
    if (this.#view === undefined) {
      this.#view = this.overlay(decodeView(w.view_bytes(this.h)))
      w.reset_touched(this.h)
    }
    return this.#view
  }
  /** Spec "View": after a local commit only the touched blocks are re-rendered (crdt_rs take_touched); the rest of
   * the previous view is shared. Views are readonly; a whole render is the only one that dedupes the duplicate ids
   * concurrent copies can leave (#renderRemote). */
  #patchView(): void {
    if (this.#view === undefined) return
    this.#completePatch(this.#view, w.view_patch(this.h))
  }
  #completePatch(previous: Json, bytes: Uint8Array): void {
    const patch = decodeView(bytes) as unknown as ViewPatch
    const next = 'full' in patch ? undefined : tryViewPatch(previous, patch)
    if (next === undefined) { this.#renderSharing(previous); return }
    this.#view = this.overlay(next)
    if (VIEW_CHECK && !Bun.deepEquals(withoutRuntime(this.#view), JSON.parse(w.view(this.h)), true)) {
      throw new Error(`View patch differs from the full view: ${w.view(this.h)}`)
    }
    w.reset_touched(this.h)
  }
  /** A remote import patches the view like a local commit (crdt_wasm `view_patch_remote`: the blocks the import
   * touched, the rest shared; ROOT attrs whole). Only a whole render is right when the view before or after holds
   * duplicate block ids (concurrent copies: the whole render dedupes them) or a block moved; that render gives every
   * block it did not change its previous object back, so a view that renders per block identity re-renders only what
   * the peer touched (y-prosemirror sync-plugin.js:592 `_typeChanged` likewise rebuilds the whole fragment and takes
   * each untouched type's previous node from its mapping, :697). */
  #renderRemote(previous: Json): void {
    this.#completePatch(previous, w.view_patch_remote(this.h))
  }
  /** The whole render, every block it did not change given its previous object back (a remote fallback, follower repair). */
  #renderSharing(previous: Json): void {
    // Both sides carry runtime fields: the previous view is overlaid, so the fresh render is overlaid before comparing.
    this.#view = shareUnchanged(this.overlay(decodeView(w.view_bytes(this.h))), blocksById(previous))
    if (VIEW_CHECK && !Bun.deepEquals(withoutRuntime(this.#view), JSON.parse(w.view(this.h)), true)) {
      throw new Error(`Shared remote view differs from the full view: ${w.view(this.h)}`)
    }
    w.reset_touched(this.h)
  }
  /** @internal */ invalidate(): void { this.#view = undefined }

  version(): Version {
    const vector = JSON.parse(w.state_vector(this.h)) as Record<string, number>
    return { epoch: w.epoch(this.h), vector: new Map(Object.entries(vector).map(([client, clock]) => [Number(client), clock])) }
  }

  get nextCounter(): number { return this.version().vector.get(this.#client) ?? 0 }

  encodeSnapshot(): Snapshot { return new Snapshot(w.encode_snapshot(this.h)) }

  encodeUpdate(since: Version): Update {
    if (since.epoch !== w.epoch(this.h)) throw new Error('Epoch mismatch: RELOAD_REQUIRED')
    return new Update(w.encode_update(this.h, JSON.stringify(Object.fromEntries(since.vector))))
  }

  /** `repair`: this replica's A1 follower moves made by the import (see #repairFollowers), a local change the host
   * sends to peers like an edit's (the editor emits it through onChange). */
  applyUpdate(update: RemoteUpdate): { version: Version; delta: ViewDelta; missing: readonly string[]; repair?: Change } {
    if (this.#editing) throw new EditRejected('Import inside edit')
    const before = this.toJSON()
    let bytes = update instanceof Uint8Array ? update : update.raw()
    if (isSnapshot(bytes)) bytes = crdt.historyUpdate(bytes).raw()
    w.view_patch_remote_begin(this.h)
    call(() => { w.apply_update(this.h, bytes) })
    this.#renderRemote(before)
    const repair = this.#repairFollowers()
    const missing = w.pending(this.h)
    return { version: this.version(), delta: { before, after: this.toJSON() }, missing: missing ? missing.split('\n') : [], ...(repair ? { repair } : {}) }
  }

  /**
   * crdt_rs MOVE.md A1 (node-anchored insert, mover side): after an import, each node a peer placed right after a node
   * this replica moved concurrently follows that node, as this replica's own moves (no undo entry). The returned update
   * is a local change: peers converge only once it reaches them. GAP: a reload before it is sent loses it (crdt_rs
   * makes the stranded set durable next).
   */
  #repairFollowers(): Change | undefined {
    // A repair is the device member's change (the engine admits ops by user), never ''. With no session user the
    // facade leaves the repair to the engine's replication path (facade-needs F23); the stranded set stays.
    const user = author(this.#user)
    if (user === '' || call(() => w.stranded_count(this.h)) === 0) return undefined
    const before = this.toJSON()
    w.begin(this.h, user)
    let moved: number
    try { moved = call(() => w.repair_followers(this.h)) } catch (error) {
      w.abort(this.h)
      w.reset_touched(this.h)
      throw error
    }
    if (moved === 0) {
      w.end(this.h)
      return undefined
    }
    const update = new Update(w.commit(this.h))
    this.#renderSharing(before)
    return { version: this.version(), delta: { before, after: this.toJSON() }, update, changed: true, repair: true }
  }

  /** `user`: the author of this edit (facade spec "Author"): it wins over `Session.user`, which wins over `actAs`. */
  edit(fn: (tx: Edit) => void, opts?: { group?: string; user?: string }): Change {
    if (this.#editing) throw new EditRejected('Nested edit')
    const result = this.#transact(tx => {
      const value: unknown = fn(tx)
      if (value && typeof value === 'object' && 'then' in value) throw new EditRejected('Edit callback must be synchronous')
    }, opts?.group, opts?.user)
    const tokens: string[] = []
    for (const [at, section] of result.sections.entries()) {
      if (section.inverses.length === 0) continue
      const last = this.#undo.at(-1)
      // Old merges same-group sections whose local op spans touch (doc.ts:150-156): every later section of this
      // edit touches the one before it; the first touches the previous edit unless undo/redo ran in between.
      if (section.group !== undefined && last?.group === section.group && (at > 0 || result.adjacent)) {
        last.inverses.unshift(...section.inverses)
        this.#seen.set(last, result.seen)
      } else {
        this.#undo.push({ token: `${w.epoch(this.h)}/${this.#client}:${++this.#tokens}`, inverses: section.inverses, group: section.group })
        this.#seen.set(this.#undo.at(-1) as History, result.seen)
      }
      tokens.push(this.#undo.at(-1)?.token as string)
    }
    if (tokens.length > 0) result.change.historyToken = tokens.at(-1)
    if (result.sections.length > 1) result.change.historyTokens = tokens
    if (result.change.changed) this.#redo = []
    // A no-op edit adds no op span, so it keeps the previous edit's adjacency (old merges by touching spans).
    if (result.change.changed) this.#adjacent = true
    return result.change
  }

  #adjacent = false

  undo(token?: string, opts?: { user?: string }): Change | null { return this.#history(this.#undo, this.#redo, token, opts?.user) }
  redo(token?: string, opts?: { user?: string }): Change | null { return this.#history(this.#redo, this.#undo, token, opts?.user) }
  historyJSON(): { undo: readonly History[]; redo: readonly History[] } { return { undo: this.#undo, redo: this.#redo } }

  #history(from: History[], to: History[], token?: string, user?: string): Change | null {
    if (this.#editing) throw new EditRejected('History inside edit')
    const entry = from.at(-1)
    if (token !== undefined && entry?.token !== token) throw new Error(`History item is not on top: ${token}`)
    if (!entry) return null
    // A node made after the entry's latest txn (its edit, or the undo/redo that produced it) was not there for it:
    // the entry's inverses do not delete it (see Edit.invert 'delete').
    const seen = this.#seen.get(entry)
    const later = (node: string): boolean => {
      if (seen === undefined) return false
      const [client, clock] = node.split(':').map(Number)
      return clock >= (seen.get(client) ?? 0)
    }
    const result = this.#transact(tx => {
      for (const inverse of entry.inverses) tx.invert(inverse, entry.inverses, later)
      tx.placeRescued()
    }, undefined, user)
    from.pop()
    to.push({ token: entry.token, inverses: result.sections.flatMap(section => section.inverses) })
    this.#seen.set(to.at(-1) as History, result.seen)
    this.#adjacent = false
    result.change.historyToken = entry.token
    return result.change
  }

  /** History entry → the version vector after its latest txn: nodes made later are not the entry's to delete. */
  readonly #seen = new WeakMap<History, ReadonlyMap<number, number>>()

  /** Links of `units` the history held at the last prune: the map is rebuilt when it passes twice that. */
  #unitsLive = 0
  /** Keeps only the `units` links a history entry can still follow (Yjs keeps `Item.redone` for the Doc's life; this
   * map is bounded by live history instead), amortized: a rebuild runs when the map has doubled since the last one. */
  #pruneUnits(): void {
    if (this.units.size <= 2 * Math.max(this.#unitsLive, 16)) return
    const kept = new Map<string, string>()
    const keep = (unit: string | null | undefined) => {
      let current = unit ?? undefined
      while (current !== undefined && !kept.has(current)) {
        const next = this.units.get(current)
        if (next === undefined) return
        kept.set(current, next)
        current = next
      }
    }
    for (const entry of [...this.#undo, ...this.#redo]) for (const inverse of entry.inverses) {
      if (inverse.kind !== 'text' || inverse.left === undefined) continue
      keep(inverse.left)
      keep(inverse.right)
      if (inverse.first) for (let offset = 0; offset < inverse.remove; offset += 1) keep(unitAfter(inverse.first, offset))
      for (const [unit, count] of inverse.removedUnits ?? []) for (let offset = 0; offset < count; offset += 1) keep(unitAfter(unit, offset))
    }
    this.units = kept
    this.#unitsLive = kept.size
  }

  #transact(body: (tx: Edit) => void, group?: string, user?: string): { change: Change; sections: { inverses: Inverse[]; group?: string }[]; adjacent: boolean; seen: ReadonlyMap<number, number> } {
    this.#pruneUnits()
    const adjacent = this.#adjacent
    const before = this.toJSON()
    const tombstones = new Map(this.tombstones)
    const runtime = new Map(this.runtime)
    const pending = new Map(this.pending)
    this.#editing = true
    w.begin(this.h, user ?? author(this.#user))
    const tx = new Edit(this, group)
    try {
      body(tx)
      tx.flushRemoved()
      for (const [unit, next] of tx.unitForward) this.units.set(unit, next)
    } catch (error) {
      // crdt_rs Doc::abort undoes the open transaction from its journal (O(changes)); the cached view is still the
      // pre-edit one.
      w.abort(this.h)
      w.reset_touched(this.h)
      this.limbo.clear()
      this.tombstones = tombstones
      this.#restore(this.runtime, runtime)
      this.#restore(this.pending, pending)
      throw error
    } finally {
      this.#editing = false
      this.built.clear()
      tx.close()
    }
    const changed = tx.counter > 0
    let bytes: Uint8Array
    if (changed) bytes = w.commit(this.h)
    else { w.end(this.h); bytes = new Uint8Array() }
    if (changed) {
      // The in-edit view, when read after the last op, is already the committed view: reuse its objects.
      const settled = tx.settled()
      if (settled === undefined) this.#patchView()
      else {
        this.#view = settled
        w.reset_touched(this.h)
        if (VIEW_CHECK && !Bun.deepEquals(withoutRuntime(settled), JSON.parse(w.view(this.h)), true)) throw new Error('Settled in-edit view differs from the full view')
      }
      // A moved block renders whole where it lands (crdt_wasm patch_json): it gets its previous object back if unchanged.
      if (tx.moved.length > 0 && this.#view !== undefined) this.#view = shareUnchanged(this.#view, blocksById(before))
    }
    const delta: ViewDelta = { before, after: changed ? this.toJSON() : before, ...(tx.moved.length > 0 ? { movedIds: tx.moved } : {}) }
    const version = this.version()
    const change: Change = { version, delta, update: new Update(bytes), changed, ...(changed && tx.textOnly ? { textOnly: true as const } : {}) }
    return { change, sections: tx.sections(), adjacent, seen: new Map(version.vector) }
  }

  entities(list: string): readonly string[] {
    if (this.#editing) throw new EditRejected('Entities inside edit')
    return entityIds(this, list)
  }

  /** crdt_rs N3 (a88dee2): the view of node `node` (a `client:clock` id, e.g. a crdt_applied or crdt_attr_removed
   * row's node) if it is removed from the tree, itself or under a deleted ancestor; undefined for a live or unknown
   * node. Type, attrs and children stay readable until a freeze drops them; text where this replica had it. */
  readDeleted(node: string): Json | undefined {
    if (!/^\d+:\d+$/u.test(node) || !w.is_deleted(this.h, node)) return undefined
    return (JSON.parse(w.node_view(this.h, node)) as Json | null) ?? undefined
  }

  textCursor(text: string, offset: number, assoc: 'left' | 'right' = 'right'): TextCursor {
    if (this.#editing) throw new EditRejected('Cursor inside edit')
    // N2 (crdt_rs text_id_at): the cursor names a text unit, as a Yjs RelativePosition (old text-cursor.ts:1-10):
    // assoc right = the unit after the caret, left = the unit before it, so a peer insert at the caret stays on the
    // other side. No such unit (the text's edge) = a null point, the text's start or end by assoc.
    const parts = text.split('.')
    const located = resolve(this, text)
    const length = located?.kind === 'text' ? w.text_len(this.h, located.node) : 0
    const at = assoc === 'left' ? offset - 1 : offset
    const unit = located?.kind === 'text' && at >= 0 && at < length ? w.text_id_at(this.h, located.node, at) ?? null : null
    return TextCursor.fromJSON({ epoch: w.epoch(this.h), container: parts.at(-2) ?? '', point: unit, assoc })
  }

  resolveTextCursor(cursor: TextCursor): { text: string; offset: number } | null {
    const json = cursor.toJSON() as Partial<TextCursorJSON>
    // Old resolveTextCursor (text-cursor.ts:84): another epoch or a malformed cursor throws; a lost point is null.
    if (json.epoch !== w.epoch(this.h)) throw new Error(`Epoch mismatch: cursor epoch ${String(json.epoch)}, document epoch ${w.epoch(this.h)} (INV-1)`)
    if (typeof json.container !== 'string' || (json.point !== null && (typeof json.point !== 'string' || !/^\d+:\d+$/u.test(json.point)))) throw new Error('Malformed text cursor')
    if (typeof json.point === 'string') {
      // The unit's node and live index now (crdt_rs text_index_of); its block may have moved, so it is found by id.
      const found = w.text_index_of(this.h, json.point)
      if (!found) return null
      const [node, index, deleted] = found.split('\t')
      if (!w.is_visible(this.h, node)) return null
      const owner = blockPath(this.toJSON(), w.attr(this.h, node, 'id') ?? '')
      if (!owner) return null
      return { text: `${owner}.text`, offset: Number(index) + (json.assoc === 'left' && deleted === '0' ? 1 : 0) }
    }
    const path = blockPath(this.toJSON(), json.container)
    if (!path) return null
    const located = resolve(this, `${path}.text`)
    if (located?.kind !== 'text') return null
    const length = w.text_len(this.h, located.node)
    // A null point names the whole text: its start or end by assoc (old text-cursor.ts:87-91).
    return { text: `${path}.text`, offset: json.assoc === 'left' ? 0 : length }
  }
}

/** Test-only differential (EASY_CRDT_VIEW_CHECK=1): every patched view must equal a whole-view render. */
const VIEW_CHECK = globalThis.process?.env.EASY_CRDT_VIEW_CHECK === '1'
type ViewMap = { [key: string]: Json }
type ViewPatch = { full: true } | (({ content: ViewMap } | { blocks: { path: string[]; view: ViewMap }[] }) & { root?: ViewMap })
const REF = '$ref'
const isRef = (value: Json): value is { $ref: string } =>
  !!value && typeof value === 'object' && !Array.isArray(value) && typeof value[REF] === 'string' && Object.keys(value).length === 1

/** Object.freeze keeping the mutable Json type (the view's Json type has no readonly variant). */
const frozen = <T,>(value: T): T => Object.freeze(value) as T

/** Freeze new JSON subtrees; shared view subtrees have already been frozen bottom-up. */
function deepFreeze<T extends Json>(value: T): T {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value
  if (Array.isArray(value)) for (const item of value) deepFreeze(item)
  else for (const key of Object.keys(value)) deepFreeze(value[key])
  return frozen(value)
}

/** The previous view lacks a block the patch shares (the full render dedupes duplicate ids concurrent moves leave):
 * the caller renders the whole view instead. */
class ViewPatchMiss extends Error {}

/** applyViewPatch, or undefined when the patch cannot be placed on this view. */
function tryViewPatch(view: Json, patch: Exclude<ViewPatch, { full: true }>): Json | undefined {
  try {
    return applyViewPatch(view, patch)
  } catch (error) {
    if (error instanceof ViewPatchMiss) return undefined
    throw error
  }
}

function hasRuntimeKeys(value: Json | undefined): boolean {
  if (Array.isArray(value)) return value.some(item => hasRuntimeKeys(item))
  if (!value || typeof value !== 'object') return false
  return Object.entries(value).some(([key, field]) => key.startsWith('$') || hasRuntimeKeys(field))
}

/** Reads an input's `$` fields onto the blocks it built: the view has the input's shape minus dropped fields, with
 * engine ids, so the walk aligns by key and by index (an array whose length changed is skipped). */
function captureRuntime(input: Json | undefined, view: Json | undefined, into: Map<string, Record<string, Json>>): void {
  if (Array.isArray(input)) {
    if (!Array.isArray(view) || view.length !== input.length) return
    for (const [at, item] of input.entries()) captureRuntime(item, view[at], into)
    return
  }
  if (!input || typeof input !== 'object' || !view || typeof view !== 'object' || Array.isArray(view)) return
  const runtime = Object.fromEntries(Object.entries(input).filter(([key]) => key.startsWith('$')))
  if (Object.keys(runtime).length > 0 && typeof view.id === 'string') into.set(view.id, { ...into.get(view.id), ...runtime })
  for (const [key, field] of Object.entries(input)) if (!key.startsWith('$')) captureRuntime(field, view[key], into)
}

/** The view with each block's runtime fields; unchanged subtrees are shared, new objects frozen. */
/** Caller-owned values may be shallow-frozen; establish the deep invariant before sharing them. */
function freezeRuntimeValue(value: Json): Json {
  if (!value || typeof value !== 'object') return value
  if (Array.isArray(value)) for (const item of value) freezeRuntimeValue(item)
  else for (const key of Object.keys(value)) freezeRuntimeValue(value[key])
  return frozen(value)
}

function overlayRuntime(view: Json, doc: Doc): Json {
  const walk = (node: Json): Json => {
    if (Array.isArray(node)) {
      const next = node.map(item => walk(item))
      return next.every((item, at) => item === node[at]) ? node : frozen<Json>(next)
    }
    if (!node || typeof node !== 'object') return node
    const id = typeof node.id === 'string' ? node.id : undefined
    const input = id === undefined ? undefined : doc.pending.get(id)
    if (id !== undefined && input !== undefined) {
      doc.pending.delete(id)
      captureRuntime(input, node, doc.runtime)
    }
    const runtime = id === undefined ? undefined : doc.runtime.get(id)
    let changed = false
    const next: Record<string, Json> = {}
    for (const [key, field] of Object.entries(node)) {
      if (key.startsWith('$')) {
        // A removed runtime field leaves the view; a kept one takes the overlay's value below.
        if (runtime?.[key] === undefined) changed = true
        continue
      }
      const value = walk(field)
      if (value !== field) changed = true
      setOwn(next, key, value)
    }
    if (runtime) for (const [key, field] of Object.entries(runtime)) {
      if (node[key] === field) next[key] = field
      else {
        changed = true
        next[key] = freezeRuntimeValue(field)
      }
    }
    return changed ? frozen<Json>(next) : node
  }
  return walk(view)
}

/** The view without runtime fields (what the engine renders), for the VIEW_CHECK differential. */
function withoutRuntime(view: Json): Json {
  if (Array.isArray(view)) return view.map(item => withoutRuntime(item))
  if (!view || typeof view !== 'object') return view
  return Object.fromEntries(Object.entries(view).filter(([key]) => !key.startsWith('$')).map(([key, field]) => [key, withoutRuntime(field)]))
}

function applyViewPatch(view: Json, patch: Exclude<ViewPatch, { full: true }>): Json {
  const root = view as ViewMap
  const content = root.content as ViewMap | undefined
  let next = content
  if ('content' in patch) next = materialize(patch.content, content) as ViewMap
  else for (const { path, view: block } of patch.blocks) {
    if (!next) throw new ViewPatchMiss('View patch without content')
    next = replaceAt(next, path, block)
  }
  // ROOT attrs written: they come whole (`root`), content stays shared.
  if (patch.root) return deepFreeze<Json>({ ...(patch.root as ViewMap), ...(next === undefined ? {} : { content: next as Json }) })
  return next === content ? view : frozen<Json>({ ...root, content: next as Json })
}

/** Path copy: `path` names block ids from content down to the changed block's parent. */
function replaceAt(at: ViewMap, path: readonly string[], block: ViewMap): ViewMap {
  const id = path[0] ?? (block.id as string)
  const slot = locate(at, id)
  if (!slot) throw new Error(`View patch: no block ${id}`)
  const child = slot.get() as ViewMap
  return slot.set(path.length === 0 ? materialize(block, child) : replaceAt(child, path.slice(1), block))
}

/** First index of each block id in a frozen view array, built once per array and carried to the copy `with` makes
 * (same positions), so a keystroke's path copy does not scan the page's top-level block list. */
const positions = new WeakMap<Json[], Map<string, number>>()
function positionsOf(list: Json[]): Map<string, number> {
  let map = positions.get(list)
  if (!map) {
    map = new Map()
    for (let index = 0; index < list.length; index++) {
      const item = list[index]
      const id = item && typeof item === 'object' && !Array.isArray(item) ? item.id : undefined
      if (typeof id === 'string' && !map.has(id)) map.set(id, index)
    }
    positions.set(list, map)
  }
  return map
}
function replaced(list: Json[], index: number, value: Json): Json[] {
  const next = frozen(list.with(index, value))
  positions.set(next, positionsOf(list))
  return next
}

/** A child block's place among an object's list fields (`children`, slots, `opts` lists of lists). */
function locate(at: ViewMap, id: string): { get(): Json; set(value: Json): ViewMap } | undefined {
  for (const [key, value] of Object.entries(at)) {
    if (key === 'text' || !Array.isArray(value)) continue
    const index = positionsOf(value).get(id)
    if (index !== undefined) return { get: () => value[index], set: replacement => frozen({ ...at, [key]: replaced(value, index, replacement) }) }
    for (let outer = 0; outer < value.length; outer++) {
      const item = value[outer]
      if (!Array.isArray(item)) continue
      const inner = positionsOf(item).get(id)
      if (inner !== undefined) return { get: () => item[inner], set: replacement => frozen({ ...at, [key]: replaced(value, outer, replaced(item, inner, replacement)) }) }
    }
  }
  return undefined
}

/** A rendered object from view_patch: `{$ref: id}` becomes the previous view's child with that id; new objects are
 * frozen, shared ones are already frozen. A decoded container without a `$ref` is new and already frozen whole (an
 * inserted block renders whole), so it is the view's object as it is. */
function materialize(value: Json, previous: Json | undefined): Json {
  if (!value || typeof value !== 'object') return value
  if (!holdsRefs(value)) return value
  const old = new Map<string, Json>()
  if (previous && typeof previous === 'object' && !Array.isArray(previous)) {
    for (const [key, field] of Object.entries(previous)) {
      if (key === 'text' || !Array.isArray(field)) continue
      for (const item of field) for (const entry of Array.isArray(item) ? item : [item]) {
        const id = (entry as ViewMap | null)?.id
        if (typeof id === 'string') old.set(id, entry)
      }
    }
  }
  const item = (entry: Json): Json => {
    if (Array.isArray(entry)) return frozen(entry.map(item))
    if (isRef(entry)) {
      const shared = old.get(entry.$ref)
      if (shared === undefined) throw new ViewPatchMiss(`View patch: no previous block ${entry.$ref}`)
      return shared
    }
    return entry && typeof entry === 'object' ? materialize(entry, old.get(entry.id as string)) : entry
  }
  if (Array.isArray(value)) return frozen(value.map(item))
  const out: ViewMap = {}
  for (const [key, field] of Object.entries(value)) setOwn(out, key, Array.isArray(field) && key !== 'text' ? frozen(field.map(item)) : deepFreeze(field))
  return frozen(out)
}

/** `out[key] = value` that keeps an own `__proto__` key own (JSON.parse and decodeView do), never the object's prototype. */
function setOwn(out: Record<string, Json>, key: string, value: Json): void {
  if (key === '__proto__') Object.defineProperty(out, key, { value, enumerable: true, writable: true, configurable: true })
  else out[key] = value
}

/** Every block object of `view` by id. */
function blocksById(view: Json): Map<string, ViewMap> {
  const out = new Map<string, ViewMap>()
  const visit = (value: Json): void => {
    if (Array.isArray(value)) { for (const item of value) visit(item); return }
    if (!value || typeof value !== 'object') return
    if (typeof value.id === 'string' && value.id) out.set(value.id, value)
    for (const [key, child] of Object.entries(value)) if (key !== 'text' && child && typeof child === 'object') visit(child)
  }
  visit(view)
  return out
}

/** `next` (a fresh render) with every block equal to its `previous` object replaced by that object (Immer-style
 * structural sharing, bottom-up: a block is shared when its fields are equal and its child blocks are shared). */
function shareUnchanged(next: Json, previous: Map<string, ViewMap>): Json {
  const visit = (value: Json): Json => {
    if (Array.isArray(value)) {
      const out = value.map(visit)
      return frozen(out)
    }
    if (!value || typeof value !== 'object') return value
    const out: ViewMap = {}
    for (const [key, field] of Object.entries(value)) {
      setOwn(out, key, key !== 'text' && field && typeof field === 'object' ? visit(field) : deepFreeze(field))
    }
    const old = typeof out.id === 'string' ? previous.get(out.id) : undefined
    if (old && sameFields(out, old)) return old
    return frozen(out)
  }
  return visit(next)
}

/** Deep equality of plain JSON (text parts carry ids, so `same` cannot compare them); runs in browsers, unlike Bun.deepEquals. */
function equalJson(a: Json | undefined, b: Json | undefined): boolean {
  if (a === b) return true
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object' || Array.isArray(a) !== Array.isArray(b)) return false
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((item, index) => equalJson(item, b[index]))
  const keys = Object.keys(a)
  return keys.length === Object.keys(b).length && keys.every(key => Object.hasOwn(b, key) && equalJson((a as ViewMap)[key], (b as ViewMap)[key]))
}

/** Equal fields; a child block counts as equal only when it is the shared previous object. */
function sameFields(next: ViewMap, old: ViewMap): boolean {
  const same = (a: Json | undefined, b: Json | undefined, top: boolean): boolean => {
    if (a === b) return true
    if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false
    if (Array.isArray(a) || Array.isArray(b)) {
      return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((item, index) => same(item, b[index], false))
    }
    if (!top && typeof a.id === 'string' && a.id) return false
    const keys = Object.keys(a)
    return keys.length === Object.keys(b).length && keys.every(key => Object.hasOwn(b, key) && (key === 'text' ? equalJson(a[key], b[key]) : same(a[key], b[key], false)))
  }
  return same(next, old, true)
}

/** Path of the block with `id` in a view (`content.children.<id>...`). */
function blockPath(view: Json, id: string): string | undefined {
  const visit = (value: Json, path: string): string | undefined => {
    if (Array.isArray(value)) {
      for (const [index, item] of value.entries()) {
        if (item && typeof item === 'object' && !Array.isArray(item) && item.id === id) return `${path}.${id}`
        // A list of lists (question `opts`) addresses its inner lists as `[index]` (resolve's opts step).
        const nested = Array.isArray(item) ? visit(item, `${path}.[${index}]`) : item && typeof item === 'object' ? visit(item, `${path}.${String((item as Record<string, Json>).id)}`) : undefined
        if (nested) return nested
      }
    } else if (value && typeof value === 'object') {
      for (const [key, child] of Object.entries(value)) {
        if (key === 'text' || !child || typeof child !== 'object') continue
        const found = visit(child, path ? `${path}.${key}` : key)
        if (found) return found
      }
    }
    return undefined
  }
  return visit(view, '')
}

function nestedItemType(doc: Doc, node: string): string {
  const containerType = w.node_type(doc.h, node) ?? ''
  const existing = children(doc.h, node)[0]
  if (existing) return existing.type
  const [type, field] = containerType.split('.')
  const singular = field?.endsWith('s') ? field.slice(0, -1) : undefined
  return singular && ![4, 5].includes(w.field_kind(type, singular)) ? `${type}.${singular}` : `${containerType}.item`
}

function resolve(doc: Doc, path: string): Locus | undefined {
  let at: Locus = { kind: 'map', node: 'root', type: '' }
  for (const part of path ? path.split('.') : []) {
    if (at.kind === 'map') {
      const here: { node: string; type: string } = at
      const kind = w.field_kind(here.type, part)
      if (kind === 6) {
        const content = children(doc.h, 'root').find(child => child.type === 'content')
        if (!content) return undefined
        at = { kind: 'map', node: content.node, type: 'content' }
      } else if (kind === 3) at = { kind: 'list', node: at.node, own: at.type !== 'content' }
      else if (kind === 2) at = { kind: 'text', node: at.node }
      else if (kind === 4) {
        const slot = children(doc.h, here.node).find(child => child.type === `${here.type}.${part}`)
        if (!slot) return undefined
        at = { kind: 'list', node: slot.node, own: false }
      } else if (kind === 5) {
        const opts = children(doc.h, here.node).find(child => child.type === `${here.type}.${part}`)
        if (!opts) return undefined
        at = { kind: 'opts', node: opts.node }
      } else if (kind === 7) {
        // Generic JSON (lexicon content.entry): the last json:field with this key; a map value is a json:map node.
        const field = children(doc.h, here.node).findLast(child => child.type === 'json:field' && w.attr(doc.h, child.node, 'key') === part)
        const map: { node: string } | undefined = field && children(doc.h, field.node).find(child => child.type === 'json:map')
        if (!field) return undefined
        at = map ? { kind: 'map', node: map.node, type: 'json:map' } : { kind: 'attr', node: field.node, type: 'json:field', key: 'value' }
      } else at = { kind: 'attr', node: at.node, type: at.type, key: part }
    } else if (at.kind === 'list') {
      const built = doc.built.get(part)
      const node: string | undefined = built !== undefined && !w.is_deleted(doc.h, built) && w.parent(doc.h, built) === at.node ? built : w.find_child(doc.h, at.node, part)
      if (!node) return undefined
      at = { kind: 'map', node, type: w.node_type(doc.h, node) ?? '' }
    } else if (at.kind === 'opts') {
      const index = /^\[(\d+)\]$/u.exec(part)
      const option: { node: string } | undefined = index ? children(doc.h, at.node)[Number(index[1])] : undefined
      if (!option) return undefined
      at = { kind: 'list', node: option.node, own: false }
    } else return undefined
  }
  return at
}

function container<K extends Locus['kind']>(doc: Doc, path: string, ...kinds: K[]): Extract<Locus, { kind: K }> {
  const at = resolve(doc, path)
  if (!at || !(kinds as string[]).includes(at.kind)) throw new EditRejected(`Expected ${kinds.join('/')} at ${path}`)
  return at as Extract<Locus, { kind: K }>
}

/** A block list, or a question's option list (`opts`: its items are the `question.opt` nodes), for remove/move. */
function itemList(doc: Doc, path: string): { node: string; own: boolean } {
  const at = container(doc, path, 'list', 'opts')
  return at.kind === 'opts' ? { node: at.node, own: false } : at
}

/** The children of `node` without the open edit's removed items (Doc.limbo). */
function present(doc: Doc, node: string): Child[] {
  const all = children(doc.h, node)
  return doc.limbo.size === 0 ? all : all.filter(child => !doc.limbo.has(child.node))
}

/** `view` without the blocks whose ids are in `ids`, unchanged branches shared. */
function withoutIds(value: Json, ids: ReadonlySet<string>): Json {
  if (Array.isArray(value)) {
    let changed = false
    const out: Json[] = []
    for (const item of value) {
      if (item && typeof item === 'object' && !Array.isArray(item) && typeof item.id === 'string' && ids.has(item.id)) { changed = true; continue }
      const next = item && typeof item === 'object' ? withoutIds(item, ids) : item
      if (next !== item) changed = true
      out.push(next)
    }
    return changed ? frozen(out) : value
  }
  if (!value || typeof value !== 'object') return value
  let out: Record<string, Json> | undefined
  for (const [key, child] of Object.entries(value)) {
    if (key === 'text' || !child || typeof child !== 'object') continue
    const next = withoutIds(child, ids)
    if (next !== child) (out ??= { ...value })[key] = next
  }
  return out === undefined ? value : frozen(out)
}

function items(doc: Doc, list: { node: string; own: boolean }): Child[] {
  const all = present(doc, list.node)
  return list.own ? all.filter(child => !isAux(child.type)) : all.filter(child => !child.type.startsWith('json:'))
}

/** crdt_rs child index of block item `at` (or after the last block item). */
function childIndex(doc: Doc, list: { node: string; own: boolean }, at: number, skip?: string): number {
  // Move indices exclude the moved node from the full sequence.
  // Limbo items are still in the crdt_rs sequence: they count for the index, never as an item.
  const all = children(doc.h, list.node).filter(child => child.node !== skip)
  const blocks = (list.own ? all.filter(child => !isAux(child.type)) : all).filter(child => !doc.limbo.has(child.node))
  if (at < blocks.length) return all.indexOf(blocks[at])
  return blocks.length === 0 ? (list.own ? 0 : all.length) : all.indexOf(blocks[blocks.length - 1]) + 1
}

function entityIds(doc: Doc, list: string): string[] {
  const at = resolve(doc, list)
  if (at?.kind === 'opts') return children(doc.h, at.node).map(child => child.node)
  return at?.kind === 'list' ? items(doc, at).map(child => child.id) : []
}

function index(value: number, min: number, max: number): void {
  if (!Number.isInteger(value) || value < min || value > max) throw new EditRejected(`Index ${value} outside [${min}, ${max}]`)
}

export class Edit {
  /** Units this edit's undo/redo re-inserted (old -> new), merged into `Doc.units` when the edit succeeds. */
  readonly unitForward = new Map<string, string>()
  #follow(unit: string): string {
    let current = unit
    for (let next = this.unitForward.get(current) ?? this.doc.units.get(current); next !== undefined;
      next = this.unitForward.get(current) ?? this.doc.units.get(current)) current = next
    return current
  }

  #ops = 0
  #active = true
  #view: Json | undefined
  readonly #inverses: Inverse[] = []
  readonly #cuts: { at: number; group?: string }[] = []
  /** @internal */ readonly moved: string[] = []
  constructor(private readonly doc: Doc, group?: string) { this.#cuts.push({ at: 0, group }) }

  get counter(): number { return this.#ops }
  /** @internal Every op so far edited text. */
  get textOnly(): boolean { return !this.#opaque && this.#inverses.every(inverse => inverse.kind === 'text') }
  /** Old `Edit.ops` (the op journal); callers read only `.length` as a write counter (editor write-document.ts:94). */
  get ops(): { readonly length: number } { return { length: this.#ops } }
  close(): void { this.#active = false }

  #op(inverse: Inverse | Inverse[]): void {
    if (!this.#active) throw new EditRejected('Closed transaction')
    this.#ops += 1
    this.#view = undefined
    const list = Array.isArray(inverse) ? inverse : [inverse]
    for (const each of list) {
      this.#touch(each.kind === 'attr' || each.kind === 'text' ? 'U' : 'I', each.node)
      // A move changes its old parent's list too (`I node` re-renders the new one and the node whole).
      if (each.kind === 'move') this.#touch('U', each.parent)
    }
    this.#inverses.push(...list)
  }

  /** Nodes this edit's ops touched, for the in-edit view patch (crdt_wasm view_patch_nodes; `R` = ROOT attrs); an op
   * with no inverse makes the in-edit view a whole render. */
  #touched: string[] = []
  #opaque = false
  #touch(kind: 'U' | 'I', node: string): void {
    this.#touched.push(node === 'root' ? 'R' : `${kind} ${node}`)
  }

  historyGroup(group?: string): void {
    if (this.#cuts.at(-1)?.at === this.#inverses.length) this.#cuts.pop()
    this.#cuts.push({ at: this.#inverses.length, group })
  }

  /** @internal */ sections(): { inverses: Inverse[]; group?: string }[] {
    return this.#cuts.flatMap((cut, at) => {
      const end = this.#cuts[at + 1]?.at ?? this.#inverses.length
      return end === cut.at ? [] : [{ inverses: this.#inverses.slice(cut.at, end).reverse(), group: cut.group }]
    })
  }

  delta(): ViewDelta { return { before: this.doc.toJSON(), after: this.#draft(), ...(this.moved.length > 0 ? { movedIds: [...this.moved] } : {}) } }

  /** @internal */ settled(): Json | undefined { return this.#ops > 0 && !this.#opaque ? this.#view : undefined }

  /** The view inside this edit: the pre-edit view with the touched blocks re-rendered (unchanged blocks shared). */
  #draft(): Json {
    if (this.#view !== undefined) return this.#view
    if (this.doc.limbo.size === 0) return this.#view = this.#render()
    return this.#view = withoutIds(this.#render(), new Set([...this.doc.limbo.values()].map(each => each.id)))
  }

  #render(): Json {
    const base = this.doc.toJSON()
    if (this.#ops === 0) return base
    if (!this.#opaque) {
      const patch = decodeView(w.view_patch_nodes(this.doc.h, this.#touched.join('\n'))) as unknown as ViewPatch
      const next = 'full' in patch ? undefined : tryViewPatch(base, patch)
      if (next !== undefined) {
        if (VIEW_CHECK && !Bun.deepEquals(withoutRuntime(next), JSON.parse(w.view(this.doc.h)), true)) {
          throw new Error(`In-edit view patch differs from the full view: ${w.view(this.doc.h)}`)
        }
        return this.doc.overlay(next)
      }
    }
    return this.doc.overlay(deepFreeze(JSON.parse(w.view(this.doc.h)) as Json))
  }

  read(path: string): Json | undefined {
    // A text is one node: its block's node_view (as readItem) instead of rendering the whole in-edit view. Only while
    // this edit removed nothing: a removed item stays in the engine until flushRemoved (Doc.limbo) and only the draft
    // hides it and its descendants.
    const text = path.endsWith('.text') && !this.#view && this.doc.limbo.size === 0 ? resolve(this.doc, path) : undefined
    if (text?.kind === 'text') return (JSON.parse(w.node_view(this.doc.h, text.node)) as Record<string, Json> | null)?.text
    let current: Json | undefined = this.#draft()
    for (const part of path ? path.split('.') : []) {
      if (Array.isArray(current)) current = /^\[\d+\]$/u.test(part) && current.every(item => Array.isArray(item))
        ? current[Number(part.slice(1, -1))]
        : current.find(item => item !== null && typeof item === 'object' && !Array.isArray(item) && item.id === part)
      else if (current && typeof current === 'object') current = current[part]
      else return undefined
    }
    return current
  }

  entities(list: string): readonly string[] { return entityIds(this.doc, list) }

  set(map: string, key: string, value: Json): void {
    if (key.startsWith('$')) { this.#runtimeKey(map, key, value); return }
    validateJson(value)
    const at = container(this.doc, map, 'map')
    this.#write(at.node, at.type, key, value)
  }

  delete(map: string, key: string): void {
    if (key.startsWith('$')) { this.#runtimeKey(map, key, undefined); return }
    const at = container(this.doc, map, 'map')
    this.#write(at.node, at.type, key, undefined)
  }

  /** A `$` field: runtime-only (requirements db-crdt-sync :61), kept in the Doc's overlay, never an engine op. */
  #runtimeKey(map: string, key: string, value: Json | undefined): void {
    if (!this.#active) throw new EditRejected('Closed transaction')
    const at = container(this.doc, map, 'map')
    const id = w.attr(this.doc.h, at.node, 'id')
    if (typeof id !== 'string') return
    const { [key]: _old, ...rest } = this.doc.runtime.get(id) ?? {}
    const next = value === undefined ? rest : { ...rest, [key]: value }
    if (Object.keys(next).length === 0) this.doc.runtime.delete(id)
    else this.doc.runtime.set(id, next)
    this.#runtimeChanged()
  }

  #runtimeChanged(): void {
    this.#view = undefined
    this.#opaque = true
    this.#ops += 1
  }

  mergeMap(map: string, patch: Record<string, Json | undefined>): void {
    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined) this.delete(map, key)
      else this.set(map, key, value)
    }
  }

  replaceContainer(map: string, key: string, value: Json): void {
    validateJson(value)
    const at = container(this.doc, map, 'map')
    if (w.field_kind(at.type, key) === 7) this.#json(at.node, key, value, false)
    else this.set(map, key, value)
  }

  /** Generic JSON map write (content keys, json:map): `merge` = old set (maps merge per key, doc.ts:773-790). */
  #json(node: string, key: string, value: Json | undefined, merge: boolean): void {
    const result = JSON.parse(call(() => w.json_set(this.doc.h, node, key, value === undefined ? undefined : JSON.stringify(value), merge))) as
      { deleted: [string, string, string | null][]; created: string[] }
    this.#undoDiff(result)
    // json_set may rewrite a field's value in place, which no inverse names
    this.#touch('U', node)
  }

  #undoDiff(result: { deleted: [string, string, string | null][]; created: string[] }): void {
    this.#op([
      ...result.deleted.map(([child, parent, left]): Inverse => {
        this.#tomb(child)
        return { kind: 'restore', node: child, parent, left, index: 0 }
      }),
      ...result.created.map((child): Inverse => ({ kind: 'delete', node: child })),
    ])
  }

  #write(node: string, type: string, key: string, value: Json | undefined): void {
    const h = this.doc.h
    const kind = w.field_kind(type, key)
    if (kind === 7) { this.#json(node, key, value, true); return }
    if (node === 'root' && key === 'content') {
      this.#undoDiff(JSON.parse(call(() => w.write(h, node, key, value === undefined ? undefined : JSON.stringify(value)))) as
        { deleted: [string, string, string | null][]; created: string[] })
      return
    }
    if (key === 'type' && node !== 'root' && type !== 'content') {
      if (typeof value !== 'string') throw new EditRejected('Expected block type')
      if (value === type) return
      // Spec "Retype" (N8): crdt_rs set_type keeps the node id, place, text and children (MOVE.md R4), so a concurrent
      // edit inside it survives. What the new type forbids is removed first with its own inverse, as the copy dropped it.
      for (const removed of [...this.doc.limbo.keys()]) {
        if (!this.#within(removed, node)) continue
        this.doc.limbo.delete(removed)
        this.#erase(removed)
      }
      const misfits = call(() => w.misfits(h, node, value))
      for (const line of misfits ? misfits.split('\n') : []) {
        if (line.startsWith('child ')) this.#remove(line.slice(6), node)
        else {
          const attr = line.slice(5)
          const raw = w.attr(h, node, attr)
          call(() => w.write(h, node, attr, undefined))
          this.#op({ kind: 'attr', node, key: attr, raw })
        }
      }
      call(() => { w.set_type(h, node, value) })
      this.#op({ kind: 'type', node, type })
      return
    }
    if (kind === 0 || kind === 1) {
      const raw = w.attr(h, node, key)
      // Deleting an absent attr writes nothing (crdt_wasm write), so it has no op and no inverse: an inverse would set
      // the attr back, e.g. `level` onto the `p` a heading was just retyped to (structure.ts:138).
      if (value === undefined && raw === undefined) return
      call(() => w.write(h, node, key, value === undefined ? undefined : JSON.stringify(value)))
      this.#op({ kind: 'attr', node, key, raw })
      return
    }
    if (kind === 2) {
      const old = utf16(w.text_utf16(h, node))
      if (typeof value === 'string') {
        // A whole-field write is the edit between the common prefix and suffix (Yjs y-prosemirror `updateYText`
        // simpleDiff), so it takes the identity edit path and its undo keeps a peer's text outside that range.
        let start = 0
        while (start < old.length && start < value.length && old[start] === value[start]) start += 1
        // Never split a surrogate pair (lib0 simpleDiffString): its halves would encode as U+FFFD.
        if (start > 0 && /[\uD800-\uDBFF]/.test(old.charAt(start - 1))) start -= 1
        let end = 0
        while (end < old.length - start && end < value.length - start && old[old.length - 1 - end] === value[value.length - 1 - end]) end += 1
        if (end > 0 && /[\uDC00-\uDFFF]/.test(old.charAt(old.length - end))) end -= 1
        this.#editTextNode(node, start, old.length - start - end, value.slice(start, value.length - end))
        return
      }
      call(() => w.write(h, node, key, value === undefined ? undefined : JSON.stringify(value)))
      this.#op({ kind: 'text', node, at: 0, remove: w.text_len(h, node), insert: old })
      return
    }
    this.#undoDiff(JSON.parse(call(() => w.write(h, node, key, value === undefined ? undefined : JSON.stringify(value)))) as
      { deleted: [string, string, string | null][]; created: string[] })
  }

  #tomb(node: string): void {
    for (const line of w.subtree_ids(this.doc.h, node).split('\n')) {
      const [id, child] = line.split(' ')
      if (id && child) this.doc.tombstones.set(id, child)
    }
  }

  insert(list: string, at: number, values: readonly Json[]): readonly string[] {
    validateJson(values)
    const target = container(this.doc, list, 'list')
    index(at, 0, items(this.doc, target).length)
    // Values land side by side: the child index is resolved once, then each next value goes after the one before,
    // until a retained value moves a node and the index is resolved again.
    let next: number | undefined
    return values.map((value, offset) => {
      if (!value || Array.isArray(value) || typeof value !== 'object') throw new EditRejected('Expected entity object')
      const retained = typeof value.id === 'string' && value.id && (w.find_block(this.doc.h, value.id) ?? this.doc.tombstones.get(value.id)) ? value.id : undefined
      if (retained) {
        // Old insert of a retained id repositions that entity with a new payload (doc.ts:838-847).
        const live = w.find_block(this.doc.h, retained)?.split('\t')
        if (live && this.doc.limbo.delete(live[0])) this.#erase(live[0])
        else if (live) this.#remove(live[0], live[1])
      }
      if (retained) next = undefined
      const child = next ?? childIndex(this.doc, target, at + offset)
      const node = call(() => w.build(this.doc.h, target.node, child, JSON.stringify(value), retained))
      next = retained ? undefined : child + 1
      this.#op({ kind: 'delete', node })
      const id = w.attr(this.doc.h, node, 'id') as string
      if (!retained) this.doc.built.set(id, node)
      if (hasRuntimeKeys(value)) this.doc.pending.set(id, value)
      return id
    })
  }

  insertAfter(list: string, after: number, values: readonly Json[]): readonly string[] {
    // GAP: old anchors on the predecessor entity (doc.ts:859); this inserts at its current index + 1.
    return this.insert(list, after + 1, values)
  }

  /**
   * Inserts blocks with no engine identity anywhere in their subtree (no nonempty `id`, no `$` key; the engine refuses
   * either before building anything) at `list`[at..], each built whole in one engine call (crdt_wasm `build_fresh`). Returns per value its blocks in document order, the value first:
   * a fresh block's id is its node id, so the caller adopts every identity without reading the views back.
   */
  insertFresh(list: string, at: number, values: readonly Json[]): readonly (readonly { id: string; type: string }[])[] {
    validateJson(values)
    const target = container(this.doc, list, 'list')
    index(at, 0, items(this.doc, target).length)
    const lines = call(() => w.build_fresh(this.doc.h, target.node, childIndex(this.doc, target, at), JSON.stringify(values)))
    const built: { id: string; type: string }[][] = []
    for (const line of lines ? lines.split('\n') : []) {
      const [kind, node, type] = line.split('\t')
      if (kind === 'R') {
        this.#op({ kind: 'delete', node })
        this.doc.built.set(node, node)
        built.push([])
      }
      built.at(-1)?.push({ id: node, type })
    }
    return built
  }

  insertLists(list: string, at: number, lists: readonly Json[][]): readonly string[] {
    const target = container(this.doc, list, 'opts')
    return lists.map((blocks, offset) => {
      const option = call(() => w.insert_node(this.doc.h, target.node, at + offset, nestedItemType(this.doc, target.node)))
      this.#op({ kind: 'delete', node: option })
      blocks.forEach((block, position) => {
        const node = call(() => w.build(this.doc.h, option, position, JSON.stringify(block), undefined))
        this.#op({ kind: 'delete', node })
      })
      return option
    })
  }

  remove(list: string, at: number, count: number): void {
    const target = itemList(this.doc, list)
    const current = items(this.doc, target)
    index(at, 0, current.length)
    index(count, 0, current.length - at)
    const left = current[at - 1]?.node ?? null
    for (const item of current.slice(at, at + count)) {
      if (item.id) {
        // Deferred to the edit's end (Doc.limbo): a reinsertion in this edit becomes a move. The undo entry takes
        // the removal's place in the history now, so undo order matches the edit's op order.
        if (!this.#active) throw new EditRejected('Closed transaction')
        const position = children(this.doc.h, target.node).findIndex(child => child.node === item.node)
        const inverse = { kind: 'restore' as const, node: item.node, parent: target.node, left, index: Math.max(0, position) }
        this.#inverses.push(inverse)
        this.doc.limbo.set(item.node, { id: item.id, inverse })
        // A deferred removal is still a write: the counter (`ops.length`) must advance, or a caller's view-derived
        // cache keyed on it keeps the pre-removal indexes (write-document `locations`). The draft hides the item.
        this.#ops += 1
        this.#view = undefined
      } else this.#remove(item.node, target.node, left)
    }
  }

  /** @internal The edit's removed items that no insertRetained took back are deleted now. */
  flushRemoved(): void {
    if (this.doc.limbo.size === 0) return
    const removed = [...this.doc.limbo.keys()]
    // A rendered draft already hides every limbo item and its descendants (#draft withoutIds), so erasing them leaves
    // it the committed view: it is kept, and the commit reuses it (settled) instead of patching the view again.
    const view = this.#view
    this.doc.limbo.clear()
    for (const node of removed) if (w.is_visible(this.doc.h, node)) this.#erase(node)
    this.#view = view
  }

  /** Deletes a limbo node whose restore inverse is already in the history. */
  #erase(node: string): void {
    this.#tomb(node)
    call(() => { w.delete_node(this.doc.h, node) })
    this.#count('I', node)
  }

  #within(node: string, ancestor: string): boolean {
    for (let at = w.parent(this.doc.h, node); at && at !== 'root'; at = w.parent(this.doc.h, at)) if (at === ancestor) return true
    return false
  }

  /** An op whose inverse is already recorded. */
  #count(kind: 'U' | 'I', node: string): void {
    if (!this.#active) throw new EditRejected('Closed transaction')
    this.#ops += 1
    this.#view = undefined
    this.#touch(kind, node)
  }

  #remove(node: string, parent: string, left?: string | null): void {
    const siblings = children(this.doc.h, parent)
    const position = siblings.findIndex(child => child.node === node)
    this.#tomb(node)
    call(() => { w.delete_node(this.doc.h, node) })
    this.#op({ kind: 'restore', node, parent, left: left === undefined ? siblings[position - 1]?.node ?? null : left, index: Math.max(0, position) })
  }

  move(list: string, from: number, to: number): void {
    const target = itemList(this.doc, list)
    const current = items(this.doc, target)
    index(from, 0, current.length - 1)
    index(to, 0, current.length - 1)
    this.#relocate(current[from].node, target, target, to)
  }

  moveTo(list: string, from: number, destination: string, at: number): void {
    if (list === destination) { this.move(list, from, at); return }
    const source = itemList(this.doc, list)
    const target = itemList(this.doc, destination)
    const current = items(this.doc, source)
    index(from, 0, current.length - 1)
    index(at, 0, items(this.doc, target).length)
    this.#relocate(current[from].node, source, target, at)
  }

  /** Explicit container join: keep original identities and redirect concurrent peer items. */
  join(list: string, options: { from: string; at?: number }): void {
    const source = itemList(this.doc, options.from)
    const target = itemList(this.doc, list)
    const at = options.at ?? items(this.doc, target).length
    index(at, 0, items(this.doc, target).length)
    this.#joinNodes(source.node, target.node, childIndex(this.doc, target, at))
  }

  #joinNodes(source: string, destination: string, at?: number, previousJoin?: string): void {
    const h = this.doc.h
    for (const child of children(h, source)) {
      const id = w.attr(h, child.node, 'id')
      if (id) this.moved.push(id)
    }
    const join = call(() => previousJoin === undefined ? w.join_node(h, source, destination, at) : w.redo_join(h, previousJoin))
    this.#opaque = true
    this.#op({ kind: 'unjoin', node: source, destination, join })
  }

  /** Spec "Move": crdt_rs move_node (N1) to `target`[at] (indexed without the node); the node keeps its id, text and
   * subtree, so a concurrent edit inside it survives the move. */
  #relocate(node: string, source: { node: string }, target: { node: string; own: boolean }, at: number): void {
    const h = this.doc.h
    const id = w.attr(h, node, 'id')
    if (id) this.moved.push(id)
    const removed = this.doc.limbo.get(node)
    const index = childIndex(this.doc, target, at, node)
    if (removed) {
      // Undo deletes the node here before undoing destination builders, then the removal's
      // restore entry restores the original node at its old parent. Both directions keep its identity.
      this.doc.limbo.delete(node)
      call(() => { w.move_node(h, node, target.node, index) })
      this.#op({ kind: 'delete', node })
      this.#touch('U', removed.inverse.parent)
      return
    }
    const left = present(this.doc, source.node).findLast((_, i, all) => all[i + 1]?.node === node)?.node ?? null
    call(() => { w.move_node(h, node, target.node, index) })
    this.#op({ kind: 'move', node, parent: source.node, left })
  }

  insertRetained(list: string, at: number, entity: string): void {
    const target = container(this.doc, list, 'list')
    const live = w.find_block(this.doc.h, entity)?.split('\t')
    if (live) {
      const [node, parent] = live
      const without = items(this.doc, target).filter(item => item.node !== node)
      this.#relocate(node, { node: parent }, target, Math.min(at, without.length))
      return
    }
    const tomb = this.doc.tombstones.get(entity)
    if (!tomb) throw new EditRejected('Missing entity')
    const lines = call(() => w.copy_subtree(this.doc.h, tomb, target.node, childIndex(this.doc, target, at))).split('\n')
    this.#op({ kind: 'delete', node: lines[0] })
  }

  readItem(entity: string): Json | undefined {
    const built = this.doc.built.get(entity)
    const node = (built !== undefined && !w.is_deleted(this.doc.h, built) ? built : undefined) ??
      w.find_block(this.doc.h, entity)?.split('\t')[0] ?? this.doc.tombstones.get(entity)
    if (!node) return undefined
    const json = JSON.parse(w.node_view(this.doc.h, node)) as Json
    if (json && this.doc.limbo.size > 0) return withoutIds(json, new Set([...this.doc.limbo.values()].map(each => each.id)))
    return json ?? undefined
  }

  setItem(entity: string, value: Record<string, Json>): void {
    validateJson(value)
    const node = w.find_block(this.doc.h, entity)?.split('\t')[0]
    if (!node) throw new EditRejected('Missing entity')
    const type = w.node_type(this.doc.h, node) ?? ''
    const current = JSON.parse(w.node_view(this.doc.h, node)) as Record<string, Json>
    for (const key of Object.keys(current)) if (key !== 'id' && key !== 'type' && !(key in value)) this.#write(node, type, key, undefined)
    for (const [key, field] of Object.entries(value)) if (key !== 'id' && key !== 'type' && !key.startsWith('$')) this.#write(node, type, key, field)
    // Replace semantics for the `$` fields too: the entity's runtime fields become the value's.
    const runtime = Object.fromEntries(Object.entries(value).filter(([key]) => key.startsWith('$')))
    if (Object.keys(runtime).length > 0 || this.doc.runtime.has(entity)) {
      if (Object.keys(runtime).length > 0) this.doc.runtime.set(entity, runtime)
      else this.doc.runtime.delete(entity)
      this.#runtimeChanged()
    }
  }

  editText(text: string, offset: number, deleteCount: number, insert: string): void {
    const target = container(this.doc, text, 'text')
    const length = w.text_len(this.doc.h, target.node)
    index(offset, 0, length)
    index(deleteCount, 0, length - offset)
    if (typeof insert !== 'string') throw new EditRejected('Expected text')
    this.#editTextNode(target.node, offset, deleteCount, insert)
  }

  #editTextNode(node: string, offset: number, deleteCount: number, insert: string): void {
    if (deleteCount === 0 && insert.length === 0) return
    const removed = deleteCount > 0 ? utf16(w.text_utf16(this.doc.h, node)).slice(offset, offset + deleteCount) : ''
    const removedUnits = unitRuns(Array.from({ length: deleteCount }, (_, index) => w.text_id_at(this.doc.h, node, offset + index) ?? ''))
    call(() => {
      if (deleteCount > 0) w.delete_text(this.doc.h, node, offset, deleteCount)
      if (insert.length > 0) w.insert_text(this.doc.h, node, offset, insert)
    })
    this.#op(textInverse(this.doc.h, node, offset, insert.length, removed, removedUnits))
  }

  /** GAP: no source provenance; the slice is plain text and relocation re-authors it. */
  sliceText(text: string, start: number, count: number): { id: string; text: string }[] {
    const target = container(this.doc, text, 'text')
    const value = utf16(w.text_utf16(this.doc.h, target.node)).slice(start, start + count)
    return value ? [{ id: '', text: value }] : []
  }

  /** Peer content an undo moved out of a node it deleted, with the sibling it followed there (see invert 'delete'). */
  readonly #rescued: string[] = []
  /** Left siblings before the first inverse ran, under every node the section made (filled on the first delete). */
  #lefts: Map<string, string[]> | undefined

  /** Moves `node` under `parent` at `at` unless crdt_blocks containment refuses it (crdt_rs checks before mutating). */
  #place(node: string, parent: string, at: number): boolean {
    try {
      call(() => { w.move_node(this.doc.h, node, parent, at) })
      return true
    } catch (error) {
      if (String(error).includes('NotAllowed')) return false
      throw error
    }
  }

  #moveRecorded(node: string, place: () => boolean): boolean {
    const h = this.doc.h
    const from = w.parent(h, node) ?? 'root'
    const left = children(h, from).findLast((_, i, all) => all[i + 1]?.node === node)?.node ?? null
    if (!place()) return false
    const id = w.attr(h, node, 'id')
    if (id) this.moved.push(id)
    this.#op({ kind: 'move', node, parent: from, left })
    return true
  }

  /** @internal After an undo/redo's inverses: each rescued node goes right after the nearest sibling it followed
   * before the undo that is live again (the section's own restores run after the delete). */
  placeRescued(): void {
    const h = this.doc.h
    for (const node of this.#rescued.splice(0)) {
      const after = this.#lefts?.get(node)?.find(left => w.is_visible(h, left))
      if (after === undefined || !w.is_visible(h, node)) continue
      const parent = w.parent(h, after) ?? 'root'
      const at = children(h, parent).filter(child => child.node !== node).findIndex(child => child.node === after) + 1
      const now = children(h, parent).findIndex(child => child.node === node)
      if (now > 0 && children(h, parent)[now - 1]?.node === after) continue
      this.#moveRecorded(node, () => this.#place(node, parent, at))
    }
  }

  /** @internal `section`: every inverse of the history entry `inverse` belongs to; `later`: whether a node was made
   * after that entry's latest txn. */
  invert(inverse: Inverse, section: readonly Inverse[], later: (node: string) => boolean): void {
    const h = this.doc.h
    if (inverse.kind === 'join') {
      this.#joinNodes(inverse.node, inverse.destination, undefined, inverse.join)
    } else if (inverse.kind === 'unjoin') {
      call(() => { w.undo_join(h, inverse.join) })
      this.#opaque = true
      this.#op({ kind: 'join', node: inverse.node, destination: inverse.destination, join: inverse.join })
    } else if (inverse.kind === 'attr') {
      const node = inverse.node
      const raw = w.attr(h, node, inverse.key)
      call(() => { w.set_attr(h, node, inverse.key, inverse.raw) })
      this.#op({ kind: 'attr', node, key: inverse.key, raw })
    } else if (inverse.kind === 'delete') {
      const node = inverse.node
      if (!w.is_visible(h, node)) return
      const parent = w.parent(h, node) ?? 'root'
      // Undo deletes only what its entry put there (coordinator 2026-10-25 Q2): a node in the subtree made after the
      // entry's latest txn (`later`) that no inverse of the entry moves back, e.g. a peer's insert or an A1 follower,
      // moves out first: to this node's place, after its left; where containment refuses that, to the nearest place up
      // the ancestors that accepts it. placeRescued then puts it after the sibling it followed.
      // A node the section also restores or moves comes back whole through its own inverse: skip its subtree.
      const kept = new Set(section.flatMap(each => each.kind === 'restore' || each.kind === 'move' ? [each.node] : []))
      const made = new Set(section.flatMap(each => each.kind === 'delete' ? [each.node] : []))
      if (this.#lefts === undefined) {
        // Before the first mutation of this undo: each node's left siblings under the nodes it deletes, nearest first.
        const lefts = new Map<string, string[]>()
        const visit = (at: string): void => {
          const list = children(h, at).map(child => child.node)
          list.forEach((child, i) => { lefts.set(child, list.slice(0, i).reverse()); visit(child) })
        }
        for (const each of made) if (w.is_visible(h, each)) visit(each)
        this.#lefts = lefts
      }
      const foreign: string[] = []
      const walk = (at: string): void => {
        for (const child of children(h, at)) {
          if (kept.has(child.node)) { /* returns with its own inverse */ } else if (later(child.node)) foreign.push(child.node)
          else walk(child.node)
        }
      }
      if (!kept.has(node)) walk(node)
      for (const each of foreign) {
        // The first place containment accepts, from this node up through its ancestors: the node's own place, the
        // end of its left sibling, the start of its right one. A place inside a node this undo deletes later is
        // rescued again from there.
        const fits = (): boolean => {
          for (let at = node; at !== 'root'; at = w.parent(h, at) ?? 'root') {
            const up = w.parent(h, at) ?? 'root'
            const siblings = children(h, up)
            const index = siblings.findIndex(child => child.node === at)
            const left = siblings[index - 1]?.node
            const right = siblings[index + 1]?.node
            if (this.#place(each, up, index) || (left !== undefined && this.#place(each, left, children(h, left).length))
              || (right !== undefined && this.#place(each, right, 0))) return true
          }
          return false
        }
        if (this.#moveRecorded(each, fits)) this.#rescued.push(each)
      }
      this.#remove(node, parent)
    } else if (inverse.kind === 'restore') {
      // crdt_rs restore_node (U1): the original node comes back with its id, attrs, text and children, so ids held
      // elsewhere (an option's entity id, cursors, a peer's view) stay valid. It returns to its own slot; the inverse's
      // place (after `left` under `parent`) wins, as the old copy's did, so a node a later op moved is moved back.
      const node = inverse.node
      call(() => { w.restore_node(h, node) })
      const parent = inverse.parent
      const siblings = children(h, parent).filter(child => child.node !== node)
      const left = inverse.left === null ? -1 : siblings.findIndex(child => child.node === inverse.left)
      const at = inverse.left === null ? 0 : left >= 0 ? left + 1 : Math.min(inverse.index, siblings.length)
      const all = children(h, parent)
      const now = all.findIndex(child => child.node === node)
      if (now < 0 || (all[now - 1]?.node ?? null) !== (siblings[at - 1]?.node ?? null)) call(() => { w.move_node(h, node, parent, at) })
      this.#op({ kind: 'delete', node })
    } else if (inverse.kind === 'move') {
      const node = inverse.node
      if (w.is_deleted_directly(h, node)) return
      const from = w.parent(h, node) ?? 'root'
      const left = children(h, from).findLast((_, i, all) => all[i + 1]?.node === node)?.node ?? null
      const parent = inverse.parent
      const siblings = children(h, parent).filter(child => child.node !== node)
      const at = inverse.left === null ? 0 : Math.max(0, siblings.findIndex(child => child.node === inverse.left) + 1)
      call(() => { w.move_node(h, node, parent, at) })
      this.#op({ kind: 'move', node, parent: from, left })
    } else if (inverse.kind === 'type') {
      const node = inverse.node
      const type = w.node_type(h, node) ?? inverse.type
      call(() => { w.set_type(h, node, inverse.type) })
      this.#op({ kind: 'type', node, type })
    } else if (inverse.left !== undefined) {
      // By identity: delete the live units the op inserted (a peer's units among them stay), then put the removed text
      // back beside the op's surviving neighbour; units are followed through earlier re-insertions (`Doc.units`).
      const node = inverse.node
      const current = utf16(w.text_utf16(h, node))
      const units = inverse.first ? Array.from({ length: inverse.remove }, (_, offset) => this.#follow(unitAfter(inverse.first as string, offset))) : []
      const owned = units.flatMap(unit => {
        const [owner, index, deleted] = w.text_index_of(h, unit)?.split('\t') ?? []
        return owner === node && deleted === '0' ? [[Number(index), unit] as const] : []
      }).toSorted((a, b) => a[0] - b[0])
      const runs: [number, number, string[]][] = []
      for (const [index, unit] of owned) {
        const last = runs.at(-1)
        if (last && last[0] + last[1] === index) { last[1] += 1; last[2].push(unit) }
        else runs.push([index, 1, [unit]])
      }
      // One inverse per separated run, anchored beside its own neighbours (Yjs redoes item by item), so a redo puts
      // a peer's units between the runs back between them.
      const restores: Inverse[] = runs.map(([at, count, units]) => ({ kind: 'text', node, at, remove: 0, insert: current.slice(at, at + count),
        first: null, left: at > 0 ? w.text_id_at(h, node, at - 1) ?? null : null, right: w.text_id_at(h, node, at + count) ?? null,
        removedUnits: unitRuns(units) }))
      call(() => { for (const [at, count] of runs.toReversed()) w.delete_text(h, node, at, count) })
      if (restores.length > 0) this.#op(restores)
      const gap = (unit: string | null | undefined, side: 'left' | 'right') =>
        unit === null || unit === undefined ? undefined : unitGap(h, node, this.#follow(unit), side)
      const at = Math.min(w.text_len(h, node), (inverse.left === null ? 0 : gap(inverse.left, 'left')) ?? gap(inverse.right, 'right') ??
        (runs[0]?.[0] ?? inverse.at))
      if (inverse.insert) {
        call(() => { w.insert_text(h, node, at, inverse.insert) })
        // The re-inserted units replace the removed ones, in order (one insert: consecutive clocks).
        const first = w.text_id_at(h, node, at)
        const old = (inverse.removedUnits ?? []).flatMap(([unit, count]) => Array.from({ length: count }, (_, offset) => unitAfter(unit, offset)))
        if (first) for (const [offset, unit] of old.entries()) if (unit) this.unitForward.set(unit, unitAfter(first, offset))
        this.#op(textInverse(h, node, at, inverse.insert.length, '', []))
      }
    } else {
      const node = inverse.node
      const current = utf16(w.text_utf16(h, node))
      const removed = current.slice(inverse.at, inverse.at + inverse.remove)
      call(() => {
        if (inverse.remove > 0) w.delete_text(h, node, inverse.at, Math.min(inverse.remove, current.length - inverse.at))
        if (inverse.insert) w.insert_text(h, node, Math.min(inverse.at, current.length - removed.length), inverse.insert)
      })
      this.#op({ kind: 'text', node, at: inverse.at, remove: inverse.insert.length, insert: removed })
    }
  }
}

export type TextCursorJSON = { epoch: number; container: string; point: string | null; assoc: 'left' | 'right' }
export class TextCursor {
  private constructor(private readonly json: Readonly<TextCursorJSON>) {}
  static fromJSON(json: TextCursorJSON): TextCursor { return new TextCursor(Object.freeze({ ...json })) }
  toJSON(): TextCursorJSON { return { ...this.json } }
}

export function canonical(value: unknown): string {
  const normalize = (v: unknown): unknown => {
    if (v instanceof Map) return [...v.entries()].toSorted(([a], [b]) => a < b ? -1 : a > b ? 1 : 0).map(([key, child]) => [key, normalize(child)])
    if (Array.isArray(v)) return v.map(child => normalize(child))
    if (v && typeof v === 'object') {
      const result: Record<string, unknown> = {}
      for (const key of Object.keys(v).toSorted()) result[key] = normalize((v as Record<string, unknown>)[key])
      return result
    }
    return v === undefined ? { absent: true } : v
  }
  return JSON.stringify(normalize(value))
}

export type Source = Encoded | { kind: 'baseline'; json: Json }

/** Old `StateJSON` header subset (state-json.ts) computed from a crdt_rs frame. `changes` lists the writers the frame
 * holds (client != 0 GENESIS) with empty `ops`: crdt_rs updates are not op lists, so callers that validated old
 * actions (commit-page-edit.ts assertPageOperation) must move to engine `crdt_applied` (U9d). */
export type FrameJSON = {
  header: { kind: 'snapshot' | 'update'; epoch: number }
  version: Record<string, number>
  baseline?: { json: Json }
  changes: { author: string; client: number; start: number; span: number; ops: readonly never[] }[]
  pending: readonly string[]
}
const frameBytes = new WeakMap<FrameJSON, Uint8Array>()

/** Full frame decodes (wasm `load`, `inspect_frame`): a test-visible count, F9 (decodeFrame decodes once). */
const decodes = { count: 0 }
function decodeSnapshot(bytes: Uint8Array, client: ReplicaId): number {
  decodes.count += 1
  return call(() => w.load(bytes, client))
}

/** inspect_frame's header from an opened doc (crdt_wasm inspect_frame): the state vector, raised by every pending
 * op's writer span ("Id { client: c, clock: k }+len ..."). */
function frameInfo(h: number): { vector: Record<string, number>; pending: string[] } {
  const vector = JSON.parse(w.state_vector(h)) as Record<string, number>
  const pending = w.pending(h).split('\n').filter(Boolean)
  for (const id of pending) {
    const match = /client: (\d+), clock: (\d+) \}\+(\d+)/u.exec(id)
    if (!match) continue
    const [, client, clock, length] = match
    vector[client] = Math.max(vector[client] ?? 0, Number(clock) + Number(length))
  }
  return { vector, pending }
}

/** `lazy`: an update's writers and pending ops decode on first read (decodeFrame: a commit reads only the header and
 * the engine validates the frame as it merges it); `crdt.decode` validates the whole frame at once. */
function decodeFrameJSON(bytes: Uint8Array, opened?: Doc, lazy = false): FrameJSON {
  type Info = { vector: Record<string, number>; pending: string[] }
  const changesOf = (info: Info) => Object.entries(info.vector).filter(([client]) => client !== '0')
    .map(([client, end]) => ({ author: client, client: Number(client), start: 0, span: end, ops: [] as never[] }))
  let json: FrameJSON
  if (isSnapshot(bytes)) {
    const doc = opened ?? Doc.wrap(decodeSnapshot(bytes, 0), { client: 0 })
    try {
      const info = frameInfo(doc.h)
      json = { header: { kind: 'snapshot', epoch: w.epoch(doc.h) }, version: info.vector, baseline: { json: doc.toJSON() }, changes: changesOf(info), pending: info.pending }
    } finally { if (!opened) doc.free() }
  } else {
    // F16: the freeze epoch the update was written in (its header; crdt_wasm inspect_frame opens it at that epoch).
    const epoch = call(() => w.update_epoch(bytes))
    // inspect_frame applies the update to a scratch doc to name its writers and pending ops.
    let info: Info | undefined
    const read = (): Info => info ??= (decodes.count += 1, JSON.parse(call(() => w.inspect_frame(bytes))) as Info)
    let changes: FrameJSON['changes'] | undefined
    json = {
      header: { kind: 'update', epoch },
      get version() { return read().vector },
      get changes() { return changes ??= changesOf(read()) },
      get pending() { return read().pending },
    }
    if (!lazy) read()
  }
  frameBytes.set(json, bytes)
  return json
}

export interface UpdateMetadata {
  epoch: number
  blocks: { client: string; start: number; length: number; user: string; own: boolean }[]
}

export const crdt = {
  /** Reads wire authors, including carried pending blocks, without importing into any shared document. */
  inspectUpdate(bytes: Uint8Array): UpdateMetadata {
    return JSON.parse(call(() => w.inspect_update(bytes))) as UpdateMetadata
  },
  decode(bytes: Uint8Array): FrameJSON { return decodeFrameJSON(bytes) },
  /** The bytes of a decoded frame (old `encode(StateJSON)`); a hand-built frame has none. */
  encode(frame: FrameJSON): Uint8Array {
    const bytes = frameBytes.get(frame)
    if (!bytes) throw new Error('GAP: only frames from crdt.decode re-encode (crdt_rs frames are not JSON)')
    return bytes
  },
  /** F9: a snapshot decodes once; the first `open()` with no session hands over that decoded doc. */
  decodeFrame(bytes: Uint8Array): { json: FrameJSON; open(session?: Session): Doc; update(): Update } {
    let decoded = isSnapshot(bytes) ? (() => { const client = newReplica(); return Doc.wrap(decodeSnapshot(bytes, client), { client }) })() : undefined
    const json = decodeFrameJSON(bytes, decoded, true)
    return {
      json,
      open: (session = {}) => {
        if (json.header.kind !== 'snapshot') throw new Error('Expected snapshot')
        if (decoded && Object.keys(session).length === 0) {
          const doc = decoded
          decoded = undefined
          return doc
        }
        return crdt.load(bytes, session)
      },
      update: () => new Update(bytes),
    }
  },
  /** Everything a snapshot holds as one update (old: its retained changes, doc.ts:1266). */
  historyUpdate(snapshot: Uint8Array | Snapshot): Update {
    const bytes = snapshot instanceof Uint8Array ? snapshot : snapshot.raw()
    if (!isSnapshot(bytes)) throw new Error('Expected snapshot')
    const handle = call(() => w.load(bytes, 0))
    try { return new Update(w.encode_update(handle, '{}')) } finally { w.free(handle) }
  },
  /** crdt_rs freeze (replaces the old server's hard baseline / epoch bridge, server/reset.ts). */
  freeze(doc: Doc): Snapshot {
    w.freeze(doc.h)
    doc.invalidate()
    return doc.encodeSnapshot()
  },
  /** crdt_rs drop_freeze_map (freeze.rs:584): forget the old-to-new id map of past freezes (the freeze job drops it
   * one week after a freeze, db-crdt-sync spec "Freeze job contract"). The view is unchanged; the next snapshot no
   * longer carries the map, and pre-freeze ids an old-epoch update names no longer translate. */
  dropFreezeMap(doc: Doc): void {
    call(() => { w.drop_freeze_map(doc.h) })
  },
  /** Freeze epoch an encoded update was written in, read from its header without opening a doc (crdt_wasm
   * `update_epoch`, crdt_rs update.rs:9). A guard compares it with the stored doc's epoch before applying. A snapshot
   * header carries no update epoch: crdt_rs returns 0 for it. */
  epochOf(source: Update | Uint8Array): number {
    const bytes = source instanceof Uint8Array ? source : source.raw()
    return call(() => w.update_epoch(bytes))
  },
  create(json: Json, session: Session = {}): Doc { return new Doc(json, undefined, session) },
  load(source: Source, session: Session = {}): Doc {
    if (!(source instanceof Uint8Array) && !(source instanceof Snapshot) && !(source instanceof Update)) return new Doc(source.json, undefined, session)
    const bytes = source instanceof Uint8Array ? source : source.raw()
    const client = session.client ?? newReplica()
    if (isSnapshot(bytes)) return Doc.wrap(decodeSnapshot(bytes, client), { ...session, client })
    const handle = w.empty(client)
    // A fresh replica has made no moves, so nothing is stranded here (A1 repair runs in Doc.applyUpdate).
    call(() => { w.apply_update(handle, bytes) })
    return Doc.wrap(handle, { ...session, client })
  },
  /** @internal Test: the user that produced op `id` (`client:clock`) in `doc` (crdt_rs user_at). */
  userAt(doc: Doc, id: string): string | undefined { return call(() => w.user_at(doc.h, id)) ?? undefined },
  /** @internal Test counter: full frame decodes so far (F9). */
  decodes(): number { return decodes.count },
  /** @internal Test counter: view objects decoded so far (view-bytes `decoded`): the render work of a view or patch. */
  viewObjects(): number { return decoded.objects },
}
